import type { TrackProcessor, ProcessorOptions } from 'livekit-client';
import { Track } from 'livekit-client';
import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision';

declare global {
  var MediaStreamTrackProcessor: any;
  var MediaStreamTrackGenerator: any;
}

export class SmoothBackgroundProcessor implements TrackProcessor<Track.Kind.Video> {
  name = 'smooth-background';
  processedTrack?: MediaStreamTrack;

  private segmenter?: ImageSegmenter;
  private processor?: any;
  private generator?: any;
  private abortController?: AbortController;
  
  private canvas: OffscreenCanvas;
  private ctx: OffscreenCanvasRenderingContext2D;
  private maskCanvas: OffscreenCanvas;
  private maskCtx: OffscreenCanvasRenderingContext2D;
  private tempCanvas: OffscreenCanvas;
  private tempCtx: OffscreenCanvasRenderingContext2D;

  private mode: 'blur' | 'image' = 'blur';
  private blurRadius = 10;
  private backgroundImage?: HTMLImageElement;
  
  private prevMaskData?: Float32Array;
  private maskImageData?: ImageData;

  constructor(opts: { mode: 'blur' | 'image', blurRadius?: number, imagePath?: string }) {
    this.mode = opts.mode;
    this.blurRadius = opts.blurRadius || 10;
    
    this.canvas = new OffscreenCanvas(640, 360);
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;
    
    this.maskCanvas = new OffscreenCanvas(640, 360);
    this.maskCtx = this.maskCanvas.getContext('2d', { willReadFrequently: true })!;
    
    this.tempCanvas = new OffscreenCanvas(640, 360);
    this.tempCtx = this.tempCanvas.getContext('2d', { willReadFrequently: true })!;

    if (this.mode === 'image' && opts.imagePath) {
      this.backgroundImage = new Image();
      this.backgroundImage.crossOrigin = 'anonymous';
      this.backgroundImage.src = opts.imagePath;
    }
  }

  async init(opts: ProcessorOptions<Track.Kind.Video>) {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm"
    );
    this.segmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter_landscape/float16/latest/selfie_segmenter_landscape.tflite",
        delegate: "GPU"
      },
      runningMode: "VIDEO",
      outputCategoryMask: true,
      outputConfidenceMasks: false,
    });

    this.processor = new globalThis.MediaStreamTrackProcessor({ track: opts.track });
    this.generator = new globalThis.MediaStreamTrackGenerator({ kind: 'video' });
    this.processedTrack = this.generator;
    this.abortController = new AbortController();

    this.processFrames(this.processor.readable, this.generator.writable, this.abortController.signal).catch(e => {
        console.error("[SmoothBackgroundProcessor] Error en el pipeline:", e);
    });
  }

  async restart(opts: ProcessorOptions<Track.Kind.Video>) {
    await this.destroy();
    await this.init(opts);
  }

  async destroy() {
    this.abortController?.abort();
    this.segmenter?.close();
    this.segmenter = undefined;
  }

  private async processFrames(
    readable: any,
    writable: any,
    signal: AbortSignal
  ) {
    const reader = readable.getReader();
    const writer = writable.getWriter();
    let frameCount = 0;

    while (!signal.aborted) {
      const { done, value: frame } = await reader.read();
      if (done || !frame) break;

      const w = frame.displayWidth;
      const h = frame.displayHeight;

      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
        this.maskCanvas.width = w;
        this.maskCanvas.height = h;
        this.tempCanvas.width = w;
        this.tempCanvas.height = h;
        this.maskImageData = new ImageData(w, h);
        this.prevMaskData = new Float32Array(w * h);
      }

      const timestamp = frame.timestamp || performance.now();
      
      this.tempCtx.globalCompositeOperation = 'copy';
      this.tempCtx.filter = 'none';
      this.tempCtx.drawImage(frame, 0, 0, w, h);

      let result;
      try {
         result = this.segmenter!.segmentForVideo(this.tempCanvas, timestamp);
      } catch (e) {
         console.error("Error segmentando:", e);
      }

      if (result && result.categoryMask) {
          const maskArray = result.categoryMask.getAsUint8Array();
          const length = maskArray.length;
          
          if (frameCount === 0) {
            console.log("[SmoothBackgroundProcessor] Mask preview:", maskArray[Math.floor(length/2)]);
          }
          frameCount++;
          
          const temporalAlpha = 0.55; 
          
          if (this.maskImageData && this.prevMaskData) {
            const data = this.maskImageData.data;
            for (let i = 0; i < length; i++) {
                // selfie_segmenter actually outputs 0 for person and >0 for background!
                const isPerson = maskArray[i] === 0 ? 1.0 : 0.0;
                
                const smoothedVal = (isPerson * (1 - temporalAlpha)) + (this.prevMaskData[i] * temporalAlpha);
                this.prevMaskData[i] = smoothedVal;
                
                const alpha = Math.max(0, Math.min(255, smoothedVal * 255));
                const offset = i * 4;
                data[offset] = 255;
                data[offset + 1] = 255;
                data[offset + 2] = 255;
                data[offset + 3] = alpha; 
            }
            this.maskCtx.putImageData(this.maskImageData, 0, 0);
          }
          
          // 1. Dibujar el fondo
          this.ctx.globalCompositeOperation = 'copy';
          if (this.mode === 'image' && this.backgroundImage && this.backgroundImage.complete) {
             this.ctx.filter = 'none';
             const imgRatio = this.backgroundImage.width / this.backgroundImage.height;
             const canvasRatio = w / h;
             let drawW = w;
             let drawH = h;
             let offsetX = 0;
             let offsetY = 0;
             if (canvasRatio > imgRatio) {
               drawH = w / imgRatio;
               offsetY = (h - drawH) / 2;
             } else {
               drawW = h * imgRatio;
               offsetX = (w - drawW) / 2;
             }
             this.ctx.drawImage(this.backgroundImage, offsetX, offsetY, drawW, drawH);
          } else {
             this.ctx.filter = `blur(${this.blurRadius}px)`;
             // IMPORTANT: Draw from tempCanvas, NOT frame, because frame is consumed in Safari
             this.ctx.drawImage(this.tempCanvas, 0, 0, w, h);
             this.ctx.filter = 'none';
          }
          
          // 2. Persona aislada
          // tempCanvas currently holds the original unblurred frame.
          // We mask it using destination-in. Since mask RGB is white, this preserves colors safely.
          this.tempCtx.globalCompositeOperation = 'destination-in';
          this.tempCtx.drawImage(this.maskCanvas, 0, 0, w, h);
          
          // 3. Pintar persona
          this.ctx.globalCompositeOperation = 'source-over';
          this.ctx.drawImage(this.tempCanvas, 0, 0, w, h);
      } else {
          // Fallback if segmenter fails
          this.ctx.globalCompositeOperation = 'copy';
          this.ctx.filter = 'none';
          this.ctx.drawImage(this.tempCanvas, 0, 0, w, h);
      }

      const outFrame = new VideoFrame(this.canvas, { timestamp: frame.timestamp });
      await writer.write(outFrame);
      frame.close();
      if (result) result.close();
    }
  }
}
